/**
 * The account panel beside the chat: "Registrieren" and "Anmelden" as tabs. Which tab opens comes
 * from the entry (entry.ts); a logout, an expired session or a deep link opens "Anmelden".
 */
import React from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import CheckIcon from '@mui/icons-material/Check';
import { SignupCard } from '../cards/SignupCard';
import { LoginCard } from '../cards/LoginCard';
import { useEntry } from '../entry-context';

export const TrustLines: React.FC<{ dark?: boolean }> = ({ dark }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  return (
    <Box className={classes.trust}>
      {['entry.trust1', 'entry.trust2', 'entry.trust3'].map((key) => (
        <Box key={key} className={cx(classes.trustRow, dark && classes.trustDark)}>
          <CheckIcon fontSize="small" className={classes.trustIcon} />
          <span>{t(key)}</span>
        </Box>
      ))}
    </Box>
  );
};

export const AuthPanel: React.FC = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { tab, openAuth } = useEntry();

  return (
    <Box className={classes.panel} component="aside" aria-label={t(tab === 'signup' ? 'entry.tabSignup' : 'entry.tabLogin')}>
      <Tabs
        value={tab}
        onChange={(_e, value) => openAuth(value, 'rail_tab')}
        variant="fullWidth"
        className={classes.tabs}
      >
        <Tab value="signup" label={t('entry.tabSignup')} className={classes.tab} />
        <Tab value="login" label={t('entry.tabLogin')} className={classes.tab} />
      </Tabs>
      <Box className={classes.body}>
        {tab === 'signup' ? (
          <>
            <Typography className={classes.heading}>{t('entry.railSignupTitle')}</Typography>
            <SignupCard place="rail" />
          </>
        ) : (
          <LoginCard place="rail" />
        )}
      </Box>
      <Box className={classes.footer}>
        <TrustLines />
      </Box>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  panel: {
    width: '100%',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.greys.lighter}`,
    borderRadius: 20,
    boxShadow: '0 24px 60px -30px rgba(20, 18, 58, 0.35)',
    overflow: 'hidden',
  },
  tabs: {
    borderBottom: `1px solid ${theme.palette.greys.lighter}`,
  },
  tab: {
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '0.95rem',
    minHeight: 52,
  },
  body: {
    padding: theme.spacing(2.5),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  heading: {
    fontWeight: 800,
    fontSize: '1.15rem',
    color: theme.palette.text.primary,
  },
  footer: {
    padding: theme.spacing(1.75, 2.5, 2.25),
    borderTop: `1px solid ${theme.palette.greys.lighter}`,
    backgroundColor: theme.palette.backgrounds.main_new,
  },
  trust: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
  },
  trustRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    fontSize: '0.825rem',
    color: theme.palette.text.secondary,
  },
  trustDark: {
    color: 'rgba(255, 255, 255, 0.85)',
  },
  trustIcon: {
    color: theme.palette.success.main,
  },
}));

export default AuthPanel;
